import React, { useMemo, useState, useEffect, useRef } from "react";
import { MarkdownViewer } from "@/components/common/MarkdownViewer";
import { useTypewriter } from "@/hooks/use-typewriter";

interface TextMessageProps {
  content: string;
  animate?: boolean;
  baseDelayMs?: number; // token başına temel gecikme
  punctuationPauseMs?: number; // noktalama sonrası ekstra gecikme
  role?: "user" | "assistant";
}

function isProbablyMarkdown(text: string) {
  // Daha güvenli markdown tespiti (yaygın noktalama işaretleri false-positive olmasın)
  return /(^#{1,6}\s)|(```)|(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^\)]+\))|(^>\s)|(^\s*[-*]\s)|(^\s*\d+\.\s)/m.test(
    text
  );
}

// Throttle hook for performance optimization
function useThrottle<T>(value: T, delay: number): T {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const lastRan = useRef<number>(Date.now());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (delay === 0) {
      // No throttle, return immediately
      setThrottledValue(value);
      return;
    }

    const now = Date.now();
    const timeSinceLastRun = now - lastRan.current;

    if (timeSinceLastRun >= delay) {
      // Enough time has passed, update immediately
      setThrottledValue(value);
      lastRan.current = now;
    } else {
      // Schedule update for remaining time
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setThrottledValue(value);
        lastRan.current = Date.now();
      }, delay - timeSinceLastRun);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return throttledValue;
}

export const TextMessage: React.FC<TextMessageProps> = ({
  content,
  animate = false,
  baseDelayMs = 20,
  punctuationPauseMs = 120,
  role = "assistant",
}) => {
  // Markdown içerikte artık animasyonu da destekleyeceğiz.
  // Ancak render bozulmalarını önlemek için Markdown tarafında tokenizasyonu satır bazlı yapacağız.
  const isMarkdown = useMemo(() => isProbablyMarkdown(content), [content]);
  const tokenRegex = useMemo(
    () => (isMarkdown ? /.*?(\n|$)/g : /\S+\s*|\n/g),
    [isMarkdown]
  );

  const { displayed, isAnimating } = useTypewriter(content, {
    enabled: animate,
    baseDelayMs,
    punctuationPauseMs: isMarkdown ? 0 : punctuationPauseMs,
    tokenRegex,
  });

  // Throttle markdown content to reduce re-renders during typing
  // Only throttle when not animating (user typing) to preserve animation smoothness
  const markdownContent = displayed || (animate ? "" : content);
  const throttledMarkdownContent = useThrottle(
    markdownContent,
    animate ? 0 : 100 // No throttle during animation, 100ms throttle during typing
  );

  // Role'a göre border radius belirleme
  const borderRadiusClass =
    role === "user"
      ? "rounded-bl-[12px] rounded-br-[12px] rounded-tl-[12px]" // User: kuyruk sağda
      : "rounded-bl-[12px] rounded-br-[12px] rounded-tr-[12px]"; // Assistant: kuyruk solda

  if (isMarkdown) {
    // Markdown'da satır satır akış gösterelim: render sırasında bozulma olmaması için.
    return (
      <div className={` relative ${borderRadiusClass} inline-block max-w-full`}>
        <div className=" py-2">
          <MarkdownViewer content={throttledMarkdownContent} />
          {isAnimating && (
            <span className="inline-block w-[6px] h-[1em] align-baseline bg-muted-foreground/60 ml-1 animate-pulse" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${
        role === "user" ? "bg-bg-secondary" : ""
      } relative ${borderRadiusClass}  inline-block max-w-full`}
    >
      <div className=" py-2">
        <p className="m-0 text-md font-normal text-foreground">
          {displayed}
          {isAnimating && (
            <span className="inline-block w-[6px] h-[1em] align-baseline bg-muted-foreground/60 ml-0.5 animate-pulse" />
          )}
        </p>
      </div>
    </div>
  );
};
